import * as React from "react";
import { observer } from "mobx-react";
import { SponsoredContentTargetInput } from "__generated__/api";
import { SponsoredContentRendererProps, SponsoredContentRendererQueryComponentProps } from "./typings";
import Renderer from "components/flexComponents/SponsoredContentRenderer/renderer";
import { withStores } from "stores";
import { useBexApi } from "@shared-ui/bex-api-context";
import { useSingleAdRequestOperationQuery } from "./hooks/useSingleAdRequestOperationQuery";
import { useSimplifiedAdOperationQuery } from "./hooks/useSimplifiedAdOperationQuery";
import { Experiment, ExperimentVariant } from "@shared-ui/experiment-context";
import { useEffect } from "react";

export const SponsoredContentRenderer = withStores(
  "advertising",
  "context"
)(
  observer(({ advertising, context }: SponsoredContentRendererProps) => {
    const pageName = advertising?.triggeredPageName;

    if (!advertising || !advertising.includeAdLoader || !pageName || typeof window === "undefined") {
      return null;
    }

    return (
      <Experiment name="MeSo_Marquee_Gallery_POC">
        <ExperimentVariant bucket={1}>
          <SingleAdRequestQueryComponent pageName={pageName} />
        </ExperimentVariant>
        <ExperimentVariant bucket={2}>
          <SimplifiedAdQueryComponent pageName={pageName} context={context} />
        </ExperimentVariant>
      </Experiment>
    );
  })
);

const SingleAdRequestQueryComponent = (props: SponsoredContentRendererQueryComponentProps) => {
  const { pageName } = props;
  const { context: bexApiContext } = useBexApi();
  const sponsoredContentTarget: SponsoredContentTargetInput = {
    pageName,
    variant: "poc",
  };
  const variables = {
    context: bexApiContext,
    sponsoredContentTarget,
  };

  const { loading, error, data } = useSingleAdRequestOperationQuery(variables);
  const marquees = data?.singleAdRequest.marquees;

  useEffect(() => {
    if (error || !marquees || (marquees && marquees.length !== 3)) {
      triggerAdLoadFailEvent();
    }
  }, [error, marquees]);

  if (loading || error || !data) {
    return null;
  }

  return <Renderer singleAdRequest={data.singleAdRequest} />;
};

const SimplifiedAdQueryComponent = (props: SponsoredContentRendererQueryComponentProps) => {
  const { context: bexApiContext } = useBexApi();
  const { pageName, context } = props;
  const sponsoredContentTarget: SponsoredContentTargetInput = {
    domain: context?.site.domain,
    pageName,
    siteId: context?.site.id.toString(),
  };
  const variables = {
    context: bexApiContext,
    sponsoredContentTarget,
    size: "1x1",
  };

  const { loading: loadingTile1, error: errorTile1, data: dataTile1 } = useSimplifiedAdOperationQuery({
    ...variables,
    slot: "NMC1TEST2",
  });
  const { loading: loadingTile2, error: errorTile2, data: dataTile2 } = useSimplifiedAdOperationQuery({
    ...variables,
    slot: "NMC2TEST2",
  });
  const { loading: loadingTile3, error: errorTile3, data: dataTile3 } = useSimplifiedAdOperationQuery({
    ...variables,
    slot: "NMC3TEST2",
  });

  useEffect(() => {
    if (
      errorTile1 ||
      errorTile2 ||
      errorTile3 ||
      (dataTile1 && !dataTile1.fetchSimplifiedAd.ad) ||
      (dataTile2 && !dataTile2.fetchSimplifiedAd.ad) ||
      (dataTile3 && !dataTile3.fetchSimplifiedAd.ad)
    ) {
      triggerAdLoadFailEvent();
    }
  }, [errorTile1, errorTile2, errorTile3, dataTile1, dataTile2, dataTile3]);

  if (loadingTile1 || errorTile1 || loadingTile2 || errorTile2 || loadingTile3 || errorTile3) {
    return null;
  }

  if (dataTile1?.fetchSimplifiedAd?.ad && dataTile2?.fetchSimplifiedAd?.ad && dataTile3?.fetchSimplifiedAd?.ad) {
    const ads = {
      __typename: "SponsoredContentSingleAdRequestResponse" as const,
      marquees: [dataTile1.fetchSimplifiedAd.ad, dataTile2.fetchSimplifiedAd.ad, dataTile3.fetchSimplifiedAd.ad],
    };

    return <Renderer singleAdRequest={ads} />;
  }

  return null;
};

const triggerAdLoadFailEvent = () => {
  window.dispatchEvent(new CustomEvent("mesoAd", { detail: { code: "AD-LOAD-FAIL" } }));
};

export default SponsoredContentRenderer;
